import * as Sentry from '@sentry/react';
import React from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

// Get DSN from Supabase secrets/environment
const getSentryDsn = async (): Promise<string | null> => {
  try {
    // Try to get from edge function that reads secrets
    const { data, error } = await supabase.functions.invoke('get-sentry-dsn');
    if (error || !data?.dsn) {
      console.warn('Could not retrieve Sentry DSN from secrets');
      return null;
    }
    return data.dsn;
  } catch (error) {
    console.warn('Failed to fetch Sentry DSN:', error);
    return null;
  }
};

// Generate release version from build timestamp or package version
const getRelease = () => {
  const buildTime = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format
  return `app@${buildTime}`;
};

export const initializeSentry = async () => {
  // Only initialize in production
  if (!import.meta.env.PROD) {
    console.log('Sentry monitoring disabled in development');
    return;
  }

  try {
    const dsn = await getSentryDsn();
    
    if (!dsn) {
      console.warn('Sentry DSN not configured - error monitoring disabled');
      return;
    }

    Sentry.init({
      dsn,
      environment: 'production',
      release: getRelease(),
      integrations: [
        // React Router integration for automatic route tracking
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        // Session Replay for debugging
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
        // Browser tracing for performance
        Sentry.browserTracingIntegration(),
      ],
      // Performance Monitoring - 10% sample rate in production
      tracesSampleRate: 0.1,
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Performance monitoring
      profilesSampleRate: 0.1,
      
      // Configure source maps
      beforeSend(event) {
        if (event.exception) {
          const error = event.exception.values?.[0];
          if (error?.type === 'UnhandledRejection') {
            console.error('Unhandled promise rejection captured by Sentry:', error);
          }
        }
        
        // Add custom tags to all events
        event.tags = {
          ...event.tags,
          build_environment: 'production',
          app_version: getRelease(),
        };
        
        return event;
      },
      
      // Initial context
      initialScope: {
        tags: {
          component: 'web-app',
          environment: 'production',
        },
      },
    });

    // Set up global error handlers
    window.addEventListener('unhandledrejection', (event) => {
      Sentry.captureException(event.reason);
    });
    
    // Add breadcrumb for app initialization
    Sentry.addBreadcrumb({
      message: 'Application initialized',
      category: 'app',
      level: 'info',
    });
    
    console.log('Sentry error monitoring initialized with advanced features');
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

// Export Sentry error boundary for React integration
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Enhanced utility functions for better error tracking
export const captureError = (error: Error, context?: {
  tags?: Record<string, string>;
  extra?: Record<string, any>;
  user?: { id: string; email?: string; role?: string };
  fingerprint?: string[];
}) => {
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
    level: 'error',
    fingerprint: context?.fingerprint,
  });
  
  if (context?.user) {
    setUserContext(context.user);
  }
};

export const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info', context?: {
  tags?: Record<string, string>;
  extra?: Record<string, any>;
}) => {
  Sentry.captureMessage(message, {
    level,
    tags: context?.tags,
    extra: context?.extra,
  });
};

// Enhanced user context with role tracking
export const setUserContext = (user: { 
  id: string; 
  email?: string; 
  role?: string;
  [key: string]: any;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.email,
  });
  
  // Set role as a tag for filtering
  if (user.role) {
    Sentry.setTag('user_role', user.role);
  }
  
  // Add breadcrumb for user context update
  addBreadcrumb(`User context set: ${user.email || user.id}`, 'auth', {
    userId: user.id,
    role: user.role,
  });
};

// Enhanced breadcrumb function with predefined categories
export const addBreadcrumb = (
  message: string, 
  category: 'navigation' | 'user' | 'auth' | 'api' | 'ui' | 'custom' = 'custom', 
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
    timestamp: Date.now() / 1000,
  });
};

// Navigation breadcrumbs
export const trackNavigation = (from: string, to: string) => {
  addBreadcrumb(`Navigated from ${from} to ${to}`, 'navigation', {
    from,
    to,
  });
};

// API call breadcrumbs
export const trackApiCall = (endpoint: string, method: string, status?: number) => {
  addBreadcrumb(`API ${method} ${endpoint}`, 'api', {
    endpoint,
    method,
    status,
  });
};

// User action breadcrumbs
export const trackUserAction = (action: string, target?: string, data?: Record<string, any>) => {
  addBreadcrumb(`User action: ${action}`, 'user', {
    action,
    target,
    ...data,
  });
};

// Performance tracking
export const startTransaction = (name: string, operation: string = 'navigation') => {
  return Sentry.startSpan({
    name,
    op: operation,
  }, () => {
    // Transaction logic here
  });
};

// Set custom tags for filtering and grouping
export const setCustomTags = (tags: Record<string, string>) => {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
};

// Clear user context (for logout)
export const clearUserContext = () => {
  Sentry.setUser(null);
  addBreadcrumb('User context cleared', 'auth');
};